// src/pages/admin/AdminAnalytics.tsx
import { useState, useEffect, useMemo } from 'react';
import {
  Container, Box, Typography, Grid, Paper, Card, CardContent,
  useTheme, alpha, CircularProgress, Tabs, Tab, Chip, Alert, Avatar,
  Divider, Slider, FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Message as MessageIcon,
  Link as ConnectionIcon,
  Store as MarketplaceIcon,
  Assessment as AssessmentIcon,
  Inventory as InventoryIcon,
  Science as ScienceIcon,
  Medication as PillIcon,
  Flight as PlaneIcon,
  DirectionsCar as CarIcon,
  Storage as ServerIcon,
  Explore as TourIcon,
  Apartment as BuildingIcon,
  AttachMoney as CurrencyIcon,
  LocalGasStation as OilIcon,
  Terrain as MiningIcon,
  Restaurant as FoodIcon,
  WbSunny as SunIcon,
  Favorite as HeartIcon,
  LocalShipping as TruckIcon,
  Analytics as AnalyticsIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  Group as GroupIcon,
  Work as WorkIcon,
  ShowChart as ShowChartIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, AreaChart, Area
} from 'recharts';
import { motion } from 'framer-motion';
import {
  getDashboardStats,
  getUserGrowth,
  getUserDistribution,
  getWeeklyActivity,
  getIndustryEngagement,
  type DashboardStats,
  type UserGrowthData,
  type UserDistribution,
  type WeeklyActivity,
  type IndustryEngagement
} from '../../services/analytics';
import * as industryApi from '../../services/industry';
import type { Industry } from '../../services/industry';
import { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { JSX } from 'react/jsx-runtime';

const MotionCard = motion(Card);
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  }
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminAnalytics = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // State for platform analytics
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [userGrowthData, setUserGrowthData] = useState<UserGrowthData[]>([]);
  const [userTypeData, setUserTypeData] = useState<UserDistribution[]>([]);
  const [activityData, setActivityData] = useState<WeeklyActivity[]>([]);
  const [industryEngagement, setIndustryEngagement] = useState<IndustryEngagement[]>([]);

  // State for industry analytics
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [yearRange, setYearRange] = useState([2020, 2024]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSubIndustries, setShowSubIndustries] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all analytics data in parallel
      const [stats, growth, distribution, activity, industry, industriesData] = await Promise.all([
        getDashboardStats(),
        getUserGrowth(),
        getUserDistribution(),
        getWeeklyActivity(),
        getIndustryEngagement(),
        industryApi.getAllIndustries({ includeSubIndustries: true })
      ]);

      setDashboardStats(stats);
      setUserGrowthData(growth);
      setUserTypeData(distribution.map(item => ({
        ...item,
        color: item.name === 'Members' ? theme.palette.primary.main : theme.palette.success.main
      })));
      setActivityData(activity);
      setIndustryEngagement(industry);
      setIndustries(industriesData);
    } catch (err: any) {
      console.error('Error loading analytics data:', err);
      setError(err.response?.data?.error || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Utility functions for industry analytics
  const getAvatarColor = (industryName: string) => {
    const colors: Record<string, string> = {
      'Pharmaceutical': '#6366f1',
      'Aerospace': '#06b6d4',
      'Automotive': '#ef4444',
      'Medical Devices': '#10b981',
      'ICT & Data Centers': '#3b82f6',
      'Finance & Banking': '#8b5cf6',
      'Tourism & Hospitality': '#f97316',
      'Real Estate': '#84cc16',
      'Petroleum & Gas': '#78716c',
      'Mining & Minerals': '#a3a3a3',
      'Food & Agriculture': '#f59e0b',
      'Renewable Energy': '#eab308',
      'default': '#6366f1',
    };
    return colors[industryName] ?? colors.default;
  };

  const getIndustryIcon = (iconName: string) => {
    const iconMap: Record<string, JSX.Element> = {
      'PillIcon': <PillIcon />,
      'PlaneIcon': <PlaneIcon />,
      'CarIcon': <CarIcon />,
      'ServerIcon': <ServerIcon />,
      'TourIcon': <TourIcon />,
      'BuildingIcon': <BuildingIcon />,
      'CurrencyIcon': <CurrencyIcon />,
      'OilIcon': <OilIcon />,
      'MiningIcon': <MiningIcon />,
      'FoodIcon': <FoodIcon />,
      'SunIcon': <SunIcon />,
      'HeartIcon': <HeartIcon />,
      'TruckIcon': <TruckIcon />
    };
    return iconMap[iconName] || <BusinessIcon />;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Localization': return '#6366f1';
      case 'Service & Infrastructure': return '#06b6d4';
      case 'Mature Industries': return '#10b981';
      default: return '#6366f1';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Localization': return <BusinessIcon />;
      case 'Service & Infrastructure': return <InventoryIcon />;
      case 'Mature Industries': return <ScienceIcon />;
      default: return <BusinessIcon />;
    }
  };

  const parseMarketSize = (marketSize: string) => {
    if (!marketSize) return 0;
    const value = parseFloat(marketSize.replace('B', '').replace('M', '').replace('K', ''));
    if (marketSize.includes('B')) return value * 1e9;
    if (marketSize.includes('M')) return value * 1e6;
    if (marketSize.includes('K')) return value * 1e3;
    return value;
  };

  const parseEmployment = (employment: string) => {
    if (!employment) return 0;
    const cleaned = employment.replace(/[^\d]/g, '');
    return parseInt(cleaned) || 0;
  };

  const formatCurrency = (value: number) => {
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return `${value.toFixed(0)}`;
  };

  const formatNumber = (value: ValueType) => {
    if (typeof value === 'number' && value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (typeof value === 'number' && value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return typeof value === 'number' ? value.toLocaleString() : value;
  };

  // Industry analytics computations
  const industryAnalytics = useMemo(() => {
    const allIndustries = industries || [];
    const allSubIndustries = allIndustries.flatMap(ind => ind.subIndustries || []);

    const totalIndustries = allIndustries.length;
    const totalSubIndustries = allSubIndustries.length;
    const totalInvestmentOpportunities = allIndustries.reduce((sum, ind) => sum + (ind.investmentOpportunities || 0), 0) +
      allSubIndustries.reduce((sum, sub) => sum + ((sub as { investmentOpportunities?: number }).investmentOpportunities || 0), 0);

    const avgGrowthRate = allIndustries.length > 0 ?
      allIndustries.reduce((sum, ind) => sum + (ind.growthRate || 0), 0) / allIndustries.length : 0;

    const totalMarketSize = allIndustries.reduce((sum, ind) => sum + parseMarketSize(ind.marketSize || '0'), 0) +
      allSubIndustries.reduce((sum, sub) => sum + parseMarketSize((sub as { marketSize?: string }).marketSize || '0'), 0);

    const totalEmployment = allIndustries.reduce((sum, ind) => sum + parseEmployment(ind.keyMetrics?.employment), 0) +
      allSubIndustries.reduce((sum, sub) => sum + parseEmployment((sub as { keyMetrics?: { employment?: string } }).keyMetrics?.employment || ''), 0);

    return {
      totalIndustries,
      totalSubIndustries,
      totalInvestmentOpportunities,
      avgGrowthRate,
      totalMarketSize,
      totalEmployment
    };
  }, [industries]);

  // Chart data preparation
  const chartData = useMemo(() => {
    const filteredIndustries = selectedCategory === 'all' ?
      industries : industries.filter(ind => ind.category === selectedCategory);

    // Growth trends data
    const growthTrendsData = filteredIndustries
      .filter(ind => ind.growthData && ind.growthData.length > 0)
      .slice(0, 5)
      .map(ind => ({
        name: ind.name,
        data: (ind.growthData || []).filter(d =>
          parseInt(d.year) >= yearRange[0] && parseInt(d.year) <= yearRange[1]
        ),
        color: getAvatarColor(ind.name)
      }));

    // Employment data
    const employmentData = filteredIndustries
      .filter(ind => ind.keyMetrics?.employment)
      .map(ind => ({
        name: ind.name.length > 15 ? ind.name.substring(0, 15) + '...' : ind.name,
        employment: parseEmployment(ind.keyMetrics?.employment || ''),
        color: getAvatarColor(ind.name)
      }))
      .sort((a, b) => b.employment - a.employment)
      .slice(0, 8);

    // Market size by category
    const categoryData = ['Localization', 'Service & Infrastructure', 'Mature Industries']
      .map(category => {
        const categoryIndustries = industries.filter(ind => ind.category === category);
        const totalSize = categoryIndustries.reduce((sum, ind) => sum + parseMarketSize(ind.marketSize || '0'), 0);
        return {
          name: category,
          value: totalSize / 1e9,
          color: getCategoryColor(category)
        };
      })
      .filter(item => item.value > 0);

    // Investment opportunities radar
    const investmentRadarData = filteredIndustries
      .slice(0, 6)
      .map(ind => ({
        industry: ind.name.length > 10 ? ind.name.substring(0, 10) + '...' : ind.name,
        opportunities: ind.investmentOpportunities || 0,
        growth: ind.growthRate || 0,
        marketSize: parseMarketSize(ind.marketSize || '0') / 1e9,
        employment: parseEmployment(ind.keyMetrics?.employment) / 1000
      }));

    return {
      growthTrendsData,
      employmentData,
      categoryData,
      investmentRadarData
    };
  }, [industries, selectedCategory, yearRange]);

  // Category breakdown data
  const categoryBreakdown = useMemo(() => {
    return ['Localization', 'Service & Infrastructure', 'Mature Industries'].map(category => {
      const categoryIndustries = industries.filter(ind => ind.category === category);
      const subIndustries = categoryIndustries.flatMap(ind => ind.subIndustries || []);

      return {
        category,
        industriesCount: categoryIndustries.length,
        subIndustriesCount: subIndustries.length,
        totalMarketSize: categoryIndustries.reduce((sum, ind) => sum + parseMarketSize(ind.marketSize || '0'), 0),
        avgGrowthRate: categoryIndustries.length > 0 ?
          categoryIndustries.reduce((sum, ind) => sum + (ind.growthRate || 0), 0) / categoryIndustries.length : 0,
        totalInvestmentOpportunities: categoryIndustries.reduce((sum, ind) => sum + (ind.investmentOpportunities || 0), 0),
        color: getCategoryColor(category),
        icon: getCategoryIcon(category)
      };
    });
  }, [industries]);

  // Platform stats cards
  const statsCards = dashboardStats ? [
    {
      title: 'Total Users',
      value: dashboardStats.users.total.toLocaleString(),
      change: `+${dashboardStats.users.new}`,
      icon: <PeopleIcon />,
      color: theme.palette.primary.main
    },
    {
      title: 'Active Connections',
      value: dashboardStats.connections.total.toLocaleString(),
      change: `+${dashboardStats.connections.thisMonth}`,
      icon: <ConnectionIcon />,
      color: theme.palette.success.main
    },
    {
      title: 'Messages Sent',
      value: dashboardStats.messages.total.toLocaleString(),
      change: `+${dashboardStats.messages.thisMonth}`,
      icon: <MessageIcon />,
      color: theme.palette.info.main
    },
    {
      title: 'Marketplace Items',
      value: dashboardStats.marketplace.total.toLocaleString(),
      change: `+${dashboardStats.marketplace.thisMonth}`,
      icon: <MarketplaceIcon />,
      color: theme.palette.warning.main
    },
  ] : [];

  const handleYearRangeChange = (event: Event, value: number | number[], activeThumb: number) => {
    if (Array.isArray(value)) {
      setYearRange(value);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Unable to load analytics data. Please try again later.
          </Typography>
          <Button variant="contained" onClick={loadData} sx={{ mt: 2 }}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        elevation={0}
        sx={{
          mb: 6,
          p: { xs: 4, md: 6 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
          position: 'relative',
          overflow: 'hidden',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
            transform: 'translateX(30%)'
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '800px', mx: 'auto' }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Analytics Dashboard
          </Typography>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              mb: 3,
              fontWeight: 500,
              opacity: 0.9
            }}
          >
            Comprehensive platform and industry insights
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <AnalyticsIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Real-time data • Interactive visualizations • Strategic insights
            </Typography>
          </Box>
        </Box>
      </MotionPaper>

      {/* Platform Stats Cards */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        sx={{ mb: 6 }}
      >
        <SectionHeader>
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Platform Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Key platform metrics and user engagement
          </Typography>
        </SectionHeader>

        <Grid container spacing={3}>
          {statsCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                elevation={2}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(stat.color, 0.1),
                        color: stat.color
                      }}
                    >
                      {stat.icon}
                    </Box>
                    <Chip
                      label={stat.change}
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </MotionBox>

      {/* Industry Summary Section */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        sx={{ mb: 6 }}
      >
        <SectionHeader>
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Industry Analytics Summary
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Aggregated metrics across all industries and sub-industries
          </Typography>
        </SectionHeader>

        <Grid container spacing={3}>
          {[
            { label: 'Total Industries', value: industryAnalytics.totalIndustries, icon: <BusinessIcon />, color: theme.palette.primary.main },
            { label: 'Sub-Industries', value: industryAnalytics.totalSubIndustries, icon: <InventoryIcon />, color: theme.palette.secondary.main },
            { label: 'Investment Opportunities', value: industryAnalytics.totalInvestmentOpportunities, icon: <TrendingUpIcon />, color: theme.palette.success.main },
            { label: 'Avg Growth Rate', value: `${industryAnalytics.avgGrowthRate.toFixed(1)}%`, icon: <ShowChartIcon />, color: theme.palette.info.main },
            { label: 'Total Market Size', value: formatCurrency(industryAnalytics.totalMarketSize), icon: <PieChartIcon />, color: theme.palette.warning.main },
            { label: 'Total Employment', value: formatNumber(industryAnalytics.totalEmployment), icon: <GroupIcon />, color: theme.palette.error.main }
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  borderRadius: 16,
                  transition: 'all 0.3s ease',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(stat.color, 0.1),
                      color: stat.color,
                      mx: 'auto',
                      mb: 2,
                      width: 56,
                      height: 56
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mb: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </MotionBox>

      {/* Controls Section */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        sx={{ mb: 6 }}
      >
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <FilterIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Analytics Controls
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category Filter</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category Filter"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Localization">Localization</MenuItem>
                  <MenuItem value="Service & Infrastructure">Service & Infrastructure</MenuItem>
                  <MenuItem value="Mature Industries">Mature Industries</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Year Range: {yearRange[0]} - {yearRange[1]}
              </Typography>
              <Slider
                value={yearRange}
                onChange={handleYearRangeChange}
                valueLabelDisplay="auto"
                min={2018}
                max={2025}
                marks={[
                  { value: 2018, label: '2018' },
                  { value: 2020, label: '2020' },
                  { value: 2022, label: '2022' },
                  { value: 2024, label: '2024' },
                  { value: 2025, label: '2025' }
                ]}
                sx={{
                  '& .MuiSlider-thumb': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiSlider-track': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showSubIndustries}
                    onChange={(e) => setShowSubIndustries(e.target.checked)}
                    color="primary"
                  />
                }
                label="Include Sub-Industries"
              />
            </Grid>
          </Grid>
        </Paper>
      </MotionBox>

      {/* Main Analytics Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Platform Analytics" icon={<AssessmentIcon />} />
          <Tab label="Industry Growth" icon={<TimelineIcon />} />
          <Tab label="Employment" icon={<GroupIcon />} />
          <Tab label="Market Distribution" icon={<PieChartIcon />} />
          <Tab label="Investment Radar" icon={<TrendingUpIcon />} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Platform Analytics Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  User Growth Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={userGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="users" stroke={theme.palette.primary.main} strokeWidth={3} name="Total Users" />
                    <Line type="monotone" dataKey="experts" stroke={theme.palette.success.main} strokeWidth={2} name="Experts" />
                    <Line type="monotone" dataKey="members" stroke={theme.palette.info.main} strokeWidth={2} name="Members" />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  User Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={userTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {userTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Weekly Activity Trends
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={activityData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8
                      }}
                    />
                    <Legend />
                    <Bar dataKey="logins" fill={theme.palette.primary.main} name="Logins" />
                    <Bar dataKey="connections" fill={theme.palette.success.main} name="New Connections" />
                    <Bar dataKey="messages" fill={theme.palette.info.main} name="Messages" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Industry Engagement Metrics
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={industryEngagement} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis type="number" stroke={theme.palette.text.secondary} />
                    <YAxis dataKey="industry" type="category" width={120} stroke={theme.palette.text.secondary} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8
                      }}
                    />
                    <Legend />
                    <Bar dataKey="views" fill={theme.palette.primary.main} name="Page Views" />
                    <Bar dataKey="interactions" fill={theme.palette.success.main} name="Interactions" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Industry Growth Tab */}
          <TabPanel value={tabValue} index={1}>
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Growth Trends - Top 5 Industries
                  </Typography>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: 'none',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                        />
                        <Legend />
                        {chartData.growthTrendsData.map((industry, index) => (
                          <Line
                            key={industry.name}
                            data={industry.data}
                            type="monotone"
                            dataKey="value"
                            stroke={industry.color}
                            strokeWidth={2}
                            name={industry.name}
                            dot={{ fill: industry.color, r: 4 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </TabPanel>

          {/* Employment Tab */}
          <TabPanel value={tabValue} index={2}>
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Employment by Industry
                  </Typography>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.employmentData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: 'none',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                          formatter={(value) => [formatNumber(value), 'Employment']}
                        />
                        <Bar dataKey="employment" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </TabPanel>

          {/* Market Distribution Tab */}
          <TabPanel value={tabValue} index={3}>
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Market Size Distribution by Category
                  </Typography>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.categoryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value.toFixed(1)}B`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {chartData.categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: 'none',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                          formatter={(value) =>
                            typeof value === 'number' ? [`${value.toFixed(1)}B`, 'Market Size'] : [value, 'Market Size']
                          }
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </TabPanel>

          {/* Investment Radar Tab */}
          <TabPanel value={tabValue} index={4}>
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Investment Opportunities Analysis
                  </Typography>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={chartData.investmentRadarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="industry" />
                        <PolarRadiusAxis />
                        <Radar
                          name="Investment Opportunities"
                          dataKey="opportunities"
                          stroke={theme.palette.primary.main}
                          fill={theme.palette.primary.main}
                          fillOpacity={0.3}
                        />
                        <Radar
                          name="Growth Rate"
                          dataKey="growth"
                          stroke={theme.palette.success.main}
                          fill={theme.palette.success.main}
                          fillOpacity={0.3}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: 'none',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                        />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </TabPanel>
        </Box>
      </Paper>

      {/* Category Breakdown */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        sx={{ mb: 6 }}
      >
        <SectionHeader>
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Category Breakdown
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Detailed analysis by industry category
          </Typography>
        </SectionHeader>

        <Grid container spacing={3}>
          {categoryBreakdown.map((category, index) => (
            <Grid item xs={12} md={4} key={category.category}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        bgcolor: category.color,
                        mr: 2,
                        width: 48,
                        height: 48
                      }}
                    >
                      {category.icon}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {category.category}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Industries</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: category.color }}>
                        {category.industriesCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Sub-Industries</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: category.color }}>
                        {category.subIndustriesCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Market Size</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formatCurrency(category.totalMarketSize)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Avg Growth</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                        {category.avgGrowthRate.toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Investment Opportunities</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                        {category.totalInvestmentOpportunities}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </MotionBox>

      {/* Key Performance Indicators */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        sx={{ mb: 6 }}
      >
        <SectionHeader>
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Key Performance Indicators
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Critical metrics for strategic decision making
          </Typography>
        </SectionHeader>

        <Grid container spacing={3}>
          {/* Top Performing Industries */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <TrendingUpIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Top Growth Industries
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {industries
                    .sort((a, b) => (b.growthRate || 0) - (a.growthRate || 0))
                    .slice(0, 5)
                    .map((industry, index) => (
                      <Box
                        key={industry.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 2,
                          borderBottom: index < 4 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none'
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: alpha(getAvatarColor(industry.name), 0.1),
                            color: getAvatarColor(industry.name),
                            mr: 2,
                            width: 40,
                            height: 40
                          }}
                        >
                          {getIndustryIcon(industry.icon || '')}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {industry.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(parseMarketSize(industry.marketSize || '0'))} market
                          </Typography>
                        </Box>
                        <Chip
                          label={`${industry.growthRate || 0}%`}
                          color="success"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    ))
                  }
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Investment Hotspots */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <WorkIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Investment Hotspots
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {industries
                    .sort((a, b) => (b.investmentOpportunities || 0) - (a.investmentOpportunities || 0))
                    .slice(0, 5)
                    .map((industry, index) => (
                      <Box
                        key={industry.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 2,
                          borderBottom: index < 4 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none'
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: alpha(getAvatarColor(industry.name), 0.1),
                            color: getAvatarColor(industry.name),
                            mr: 2,
                            width: 40,
                            height: 40
                          }}
                        >
                          {getIndustryIcon(industry.icon || '')}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {industry.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {industry.category}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${industry.investmentOpportunities || 0} ops`}
                          color="warning"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    ))
                  }
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MotionBox>

      {/* Data Insights */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)}, ${alpha(theme.palette.success.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Data Insights & Quality
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Data Coverage
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                • {industries.filter(ind => ind.growthData && ind.growthData.length > 0).length} industries with growth data
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                • {industries.filter(ind => ind.keyMetrics?.employment).length} industries with employment data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {industries.filter(ind => ind.subIndustries && ind.subIndustries.length > 0).length} industries with sub-industry breakdown
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Key Trends
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                • Highest growth: {industries.length > 0 ? industries.reduce((max, ind) => (ind.growthRate || 0) > (max.growthRate || 0) ? ind : max, industries[0] || {}).name : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                • Largest market: {industries.length > 0 ? industries.reduce((max, ind) => parseMarketSize(ind.marketSize || '0') > parseMarketSize(max.marketSize || '0') ? ind : max, industries[0] || {}).name : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Most opportunities: {industries.length > 0 ? industries.reduce((max, ind) => (ind.investmentOpportunities || 0) > (max.investmentOpportunities || 0) ? ind : max, industries[0] || {}).name : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </MotionBox>
    </Container>
  );
};

export default AdminAnalytics;
